/**
 * portalAccess.js
 * ===============
 * TASK 112 - WHO is looking, and WHAT their plan actually grants.
 *
 * THE OWNER'S DECISION (2026-11-04)
 * ---------------------------------
 * There are two kinds of user in this portal and they must be treated differently:
 *
 *   1. the ACCOUNT user (the owner/admin of a Brand account or of a
 *      Multi-Vendor / mall account) - a locked feature STAYS VISIBLE to them,
 *      with a padlock and an upgrade action that leads somewhere they can act on;
 *   2. the SELLER user (a store under a mall, a multi-vendor seller, a branch) -
 *      a locked feature is NOT shown to them at all. No teaser, no padlock, no
 *      upgrade message. The upgrade is not their business: it is something the
 *      account user does on their behalf.
 *
 * THE ROLE SIGNAL (MEASURED 2026-11-04, live /api/auth/status)
 * -----------------------------------------------------------
 *   role 2 (account admin) -> roles: ["account"],
 *   role 3 (seller)        -> roles: ["seller"].
 * The backend's own account-admin check is `isAccountAdmin` = role 1 or 2.
 * `SellerLayout` already refuses the seller portal to every role but 3
 * (role 2 -> /admin/dashboard), so a role 1/2 user is never inside /seller.
 * The frontend's own signal is therefore `user.role_id` - NOT the account type
 * (a role-3 user of a BRAND account is still a seller user, and a BRANCH is a
 * seller whose `structure_type` is 'branch').
 *
 * THE ENTITLEMENT SOURCE
 * ----------------------
 * The PLAN's entitlements, served by `/auth/status` as
 * `permissions.featureFlags` and read through `useFeatureFlags().hasFeatureFlag`.
 * The legacy `user.campaign_management` / `user.customer_management` mirrors are
 * served TRUE to everybody and are DEAD as gates - they are never read here.
 */

/** Role ids that are the ACCOUNT user (backend `isAccountAdmin`). */
export const ACCOUNT_ROLE_IDS = Object.freeze([1, 2]);

/** The role id of a SELLER user (a store under a mall, a branch, a sub-seller). */
export const SELLER_ROLE_ID = 3;

/** The ACCOUNT user's own billing surface (route exists: src/App.js). */
export const ACCOUNT_BILLING_PATH = '/admin/subscription-billing';

/**
 * The ACCOUNT user's plan-change page. The route EXISTS in `src/App.js`
 * (`subscription-billing/change-plan`). The old hardcoded
 * `/admin/subscription-billing/change-tier` matched NO route and 404'd.
 */
export const ACCOUNT_CHANGE_PLAN_PATH = '/admin/subscription-billing/change-plan';

/**
 * The three answers a nav item or a gate can give.
 *   OPEN   - the plan grants it, render it normally
 *   LOCKED - the plan does not grant it and the viewer is the ACCOUNT user:
 *            keep it visible, with a padlock and the upgrade action
 *   HIDDEN - the plan does not grant it and the viewer is a SELLER:
 *            render nothing at all (no teaser, no padlock, no upgrade message)
 */
export const FEATURE_VISIBILITY = Object.freeze({
  OPEN: 'open',
  LOCKED: 'locked',
  HIDDEN: 'hidden',
});

/**
 * The numeric role id of a user, or null when it is absent/unusable.
 * @param {object|undefined|null} user
 * @returns {number|null}
 */
export const roleIdOf = (user) => {
  const raw = user?.role_id;
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : null;
};

/**
 * @param {object|undefined|null} user
 * @returns {boolean} true only for a measured ACCOUNT user (role 1 or 2)
 */
export const isAccountUser = (user) => ACCOUNT_ROLE_IDS.includes(roleIdOf(user));

/**
 * @param {object|undefined|null} user
 * @returns {boolean} true only for a SELLER user (role 3)
 */
export const isSellerUser = (user) => roleIdOf(user) === SELLER_ROLE_ID;

/**
 * 'account' | 'seller' | 'unknown' - the viewer class the UI reasons about.
 * `unknown` is deliberately NOT treated as an account user: an upgrade action is
 * only ever offered to a viewer we have positively identified.
 * @param {object|undefined|null} user
 * @returns {'account'|'seller'|'unknown'}
 */
export const viewerKindOf = (user) => {
  if (isAccountUser(user)) return 'account';
  if (isSellerUser(user)) return 'seller';
  return 'unknown';
};

/**
 * The single decision used by the nav and by every gate.
 *
 * @param {object} params
 * @param {boolean} params.granted the PLAN's answer (hasFeatureFlag(...))
 * @param {object|null|undefined} params.user the signed-in user
 * @returns {'open'|'locked'|'hidden'}
 */
export const resolveFeatureVisibility = ({ granted, user }) => {
  if (granted === true) return FEATURE_VISIBILITY.OPEN;
  return isAccountUser(user) ? FEATURE_VISIBILITY.LOCKED : FEATURE_VISIBILITY.HIDDEN;
};

/**
 * May this viewer be offered an upgrade action at all?
 * Only the ACCOUNT user - never a seller, never an unidentified viewer.
 * @param {object|null|undefined} user
 * @returns {boolean}
 */
export const canOfferUpgrade = (user) => isAccountUser(user);

/**
 * Where the upgrade action goes: a page that EXISTS for the ACCOUNT user.
 * `null` when the viewer may not be offered an upgrade (a seller, or unknown),
 * so a caller can never render a dead link for them.
 * @param {object|null|undefined} user
 * @returns {string|null}
 */
export const upgradePathFor = (user) => (canOfferUpgrade(user) ? ACCOUNT_CHANGE_PLAN_PATH : null);

export default resolveFeatureVisibility;
